import set from 'lodash/set';

import {
  AppAdapterCallbacks,
  AppAdapterCallbacksFnTypes,
  PlaidRedirectUriSetter,
  ShowManualACH,
  ExitedNativePlaidFlow,
  NativeAddedPlaid
} from './AppAdapter';

export default class AppBridgeCallbacks implements AppAdapterCallbacks {
  private static i: AppBridgeCallbacks;

  /**
   * The Singleton's constructor should always be private to prevent direct
   * construction calls with the `new` operator.
   */
  private constructor() {
    this.callbacks = new Map<string, AppAdapterCallbacksFnTypes>();
    this.setupGlobalCallbacks();
  }

  /**
   * The static method that controls the access to the singleton instance.
   *
   * This implementation let you subclass the Singleton class while keeping
   * just one instance of each subclass around.
   */
  public static get instance(): AppBridgeCallbacks {
    if (!AppBridgeCallbacks.i) {
      AppBridgeCallbacks.i = new AppBridgeCallbacks();
    }

    return AppBridgeCallbacks.i;
  }

  /**
   * Here is where we should setup the actual global callbacks
   */

  private callbacks: Map<string, AppAdapterCallbacksFnTypes>;

  private setupGlobalCallbacks() {
    set(window, 'bridgeHandlers.setRedirectUri', (redirectUri: string) => {
      const handler = this.callbacks.get(
        'setRedirectUri'
      ) as PlaidRedirectUriSetter;

      if (handler) {
        handler(redirectUri);
      }
    });

    set(window, 'bridgeHandlers.showManualACH', () => {
      const handler = this.callbacks.get('showManualACH') as ShowManualACH;

      if (handler) {
        handler();
      }
    });

    set(window, 'bridgeHandlers.exitedNativePlaidFlow', () => {
      const handler = this.callbacks.get(
        'exitedNativePlaidFlow'
      ) as ExitedNativePlaidFlow;

      if (handler) {
        handler();
      }
    });

    set(window, 'bridgeHandlers.nativeAddedPlaid', (authToken: string) => {
      const handler = this.callbacks.get(
        'nativeAddedPlaid'
      ) as NativeAddedPlaid;

      if (handler) {
        handler(authToken);
      }
    });
  }

  /**
   * Allow components to react to changes when the bridging function is invoked. Only one callback function can
   * be active at any given time period, as this function overrides previous callbacks
   * @param bridgingFunctionName Name of the fn to respond to
   * @param callback the callback function
   */
  public subscribe(
    bridgingFunctionName: string,
    callback: AppAdapterCallbacksFnTypes
  ): void {
    this.callbacks.set(bridgingFunctionName, callback);
  }

  /**
   * Allows a component to unsubscribe to a global bridging function
   * @param bridgingFunctionName Name of the fn to unattach from
   */
  public unsubscribe(bridgingFunctionName: string): void {
    this.callbacks.delete(bridgingFunctionName);
  }
}
